<template>
  <b-card no-body class="mb-1 shadow-none">
    <b-card-header header-tag="header" role="tab">
      <h6 class="m-0 d-flex justify-content-between align-items-center">
        <a v-b-toggle.accordion-financial class="text-dark" href="javascript: void(0);">
          Financeiro
          <span v-if="records.length" class="badge bg-dark text-light ml-2 badge-count">{{ records.length }}</span>
        </a>
        <i class="bx bx-chevron-down chevron-icon" v-b-toggle.accordion-financial></i>
      </h6>
    </b-card-header>
    <b-collapse id="accordion-financial" accordion="my-accordion" role="tabpanel">
      <b-card-body>
        <div v-if="loading" class="text-center py-3">
          <i class="mdi mdi-loading mdi-spin"></i> Carregando lançamentos...
        </div>
        <template v-else>
          <div class="d-flex justify-content-between align-items-center mb-3">
            <div>
              <strong>Total ({{ baseCurrency }}):</strong>
              <span class="ml-1">{{ formatCurrency(totalBase) }}</span>
            </div>
            <div>
              <b-button
                v-if="canCreate"
                variant="primary"
                size="sm"
                @click="openCreate()">
                <i class="mdi mdi-plus mr-1"></i> Novo lançamento
              </b-button>
              <b-button
                v-if="records.length && canView"
                variant="outline-secondary"
                size="sm"
                class="ml-2"
                @click="exportXlsx()">
                <i class="mdi mdi-file-excel mr-1"></i> Exportar
              </b-button>
            </div>
          </div>

          <p v-if="!records.length" class="text-muted mb-0">
            Nenhum lançamento financeiro registrado.
          </p>

          <table v-else class="table table-sm align-middle">
            <thead>
              <tr>
                <th>Data</th>
                <th>Pagador</th>
                <th>Descrição</th>
                <th>Forma pgto</th>
                <th class="text-right">Valor ({{ baseCurrency }})</th>
                <th style="width:90px"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in records" :key="r.id">
                <td>{{ formatDate(r.transaction_date) }}</td>
                <td>{{ r.payer_name || '—' }}</td>
                <td>{{ r.description }}</td>
                <td>{{ paymentMethodName(r.payment_method) }}</td>
                <td class="text-right">{{ formatCurrency(r.amount_base) }}</td>
                <td class="text-right">
                  <b-button
                    v-if="canUpdate"
                    variant="link"
                    size="sm"
                    class="p-1"
                    @click="openEdit(r)"
                    title="Editar">
                    <i class="mdi mdi-pencil"></i>
                  </b-button>
                  <b-button
                    v-if="canDelete"
                    variant="link"
                    size="sm"
                    class="p-1 text-danger"
                    @click="confirmDelete(r)"
                    title="Excluir">
                    <i class="mdi mdi-trash-can-outline"></i>
                  </b-button>
                </td>
              </tr>
            </tbody>
          </table>
        </template>
      </b-card-body>
    </b-collapse>

    <FinancialFormModal
      v-model="showModal"
      :participantId="idParticipant"
      :record="editingRecord"
      :settings="settings"
      :participant="participant"
      @saved="onSaved" />
  </b-card>
</template>

<script>
import { mapGetters } from 'vuex'
import services from './services'
import FinancialFormModal from './FinancialFormModal.vue'
import { errorMessage, successMessage } from '@/components/alertsMessages'

export default {
  name: 'ParticipantFinancials',
  components: { FinancialFormModal },
  props: {
    idParticipant: { type: [Number, String], required: true },
    participant: { type: Object, default: () => ({}) }
  },
  data () {
    return {
      loading: false,
      records: [],
      totalBase: 0,
      baseCurrency: 'BRL',
      settings: {
        base_currency: 'BRL',
        enabled_currencies: ['BRL'],
        categories: [],
        payment_methods: []
      },
      showModal: false,
      editingRecord: null
    }
  },
  computed: {
    ...mapGetters('auth', ['hasPermission', 'isSuperAdmin']),
    canView () {
      return this.isSuperAdmin || this.hasPermission('financial_control.view')
    },
    canCreate () {
      return this.isSuperAdmin || this.hasPermission('financial_control.create')
    },
    canUpdate () {
      return this.isSuperAdmin || this.hasPermission('financial_control.update')
    },
    canDelete () {
      return this.isSuperAdmin || this.hasPermission('financial_control.delete')
    },
    categoryIndex () {
      const idx = {}
      ;(this.settings.categories || []).forEach(c => { idx[c.slug] = c })
      return idx
    },
    paymentIndex () {
      const idx = {}
      ;(this.settings.payment_methods || []).forEach(p => { idx[p.slug] = p })
      return idx
    }
  },
  mounted () {
    this.loadAll()
  },
  methods: {
    async loadAll () {
      this.loading = true
      try {
        const [settingsRes, listRes] = await Promise.all([
          services.getFinancialSettings(),
          services.listFinancials(this.idParticipant, { all: 1 })
        ])
        this.settings = settingsRes.data.data || this.settings
        this.baseCurrency = listRes.data.meta?.base_currency || this.settings.base_currency || 'BRL'
        const list = Array.isArray(listRes.data.data) ? listRes.data.data : (listRes.data.data?.data || [])
        this.records = list
        this.totalBase = Number(listRes.data.meta?.total_base || 0)
      } catch (e) {
        const msg = e?.response?.data?.message || 'Falha ao carregar lançamentos.'
        errorMessage(msg, 3000)
      } finally {
        this.loading = false
      }
    },
    openCreate () {
      this.editingRecord = null
      this.showModal = true
    },
    openEdit (record) {
      this.editingRecord = record
      this.showModal = true
    },
    onSaved () {
      this.loadAll()
    },
    async confirmDelete (record) {
      const ok = await this.$bvModal.msgBoxConfirm(
        `Excluir o lançamento de ${this.formatDate(record.transaction_date)}?`,
        { title: 'Confirmar exclusão', okTitle: 'Excluir', cancelTitle: 'Cancelar', okVariant: 'danger' }
      )
      if (!ok) return
      try {
        await services.destroyFinancial(this.idParticipant, record.id)
        successMessage('Lançamento excluído.', 2000)
        this.loadAll()
      } catch (e) {
        const msg = e?.response?.data?.message || 'Falha ao excluir.'
        errorMessage(msg, 3000)
      }
    },
    async exportXlsx () {
      try {
        const res = await services.exportFinancials(this.idParticipant)
        const url = window.URL.createObjectURL(new Blob([res.data]))
        const a = document.createElement('a')
        a.href = url
        a.download = `lancamentos-participante-${this.idParticipant}.xlsx`
        document.body.appendChild(a)
        a.click()
        a.remove()
        window.URL.revokeObjectURL(url)
      } catch (e) {
        errorMessage('Falha ao exportar.', 3000)
      }
    },
    categoryName (slug) {
      if (!slug) return '—'
      return this.categoryIndex[slug]?.name || slug
    },
    paymentMethodName (slug) {
      if (!slug) return '—'
      return this.paymentIndex[slug]?.name || slug
    },
    formatDate (date) {
      if (!date) return ''
      return this.$moment(date).format('DD/MM/YYYY')
    },
    formatCurrency (val) {
      const num = Number(val) || 0
      return num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    }
  }
}
</script>
